import { useToast } from '@airship-platform/airship-patterns';
import {
  getGroupChildren,
  isItemGroupChild,
} from '@airship-platform/appointments-feature/utils';
import { useCallback } from 'react';
import {
  AppointmentProposalOptionProduct,
  AppointmentProposalOptionProductCreate,
  useAppointmentProposalOptionProducts,
} from '../../../data-access/appointmentProposalOptionProducts';

export const useItemGroupMutations = () => {
  const {
    proposalProducts,
    createAppointmentProposalOptionProduct,
    updateAppointmentProposalOptionProduct,
    deleteAppointmentProposalOptionProduct,
    getNextSortOrder,
  } = useAppointmentProposalOptionProducts();
  const { showToast } = useToast();

  const getChildren = useCallback(
    (rootId: string) => getGroupChildren(proposalProducts, rootId),
    [proposalProducts]
  );

  const addSubItem = useCallback(
    async (
      root: AppointmentProposalOptionProduct,
      create: AppointmentProposalOptionProductCreate
    ) => {
      if (isItemGroupChild(root)) {
        showToast('Sub-items cannot be added to a sub-item', 'error');
        return;
      }

      const groupName = root.itemGroupName?.trim() || root.name.trim();
      if (!groupName) {
        showToast('Name the item before adding sub-items', 'error');
        return;
      }

      const isPromoting = !root.itemGroupName;
      if (isPromoting) {
        await updateAppointmentProposalOptionProduct(
          root.appointmentId,
          root.appointmentProposalId,
          root.appointmentProposalOptionId,
          root.appointmentProposalOptionProductId,
          { itemGroupName: groupName }
        );
      }

      await createAppointmentProposalOptionProduct(
        root.appointmentId,
        root.appointmentProposalId,
        root.appointmentProposalOptionId,
        {
          ...create,
          itemGroupRootId: root.appointmentProposalOptionProductId,
          jobId: root.jobId,
          sortOrder: getNextSortOrder(),
        }
      );

      showToast(
        `Added ${create.name} as a sub-item of ${root.name}`,
        'success'
      );
    },
    [
      createAppointmentProposalOptionProduct,
      getNextSortOrder,
      showToast,
      updateAppointmentProposalOptionProduct,
    ]
  );

  const deleteProductCascading = useCallback(
    async (product: AppointmentProposalOptionProduct) => {
      const children = getChildren(product.appointmentProposalOptionProductId);

      await Promise.all(
        children.map((child) =>
          deleteAppointmentProposalOptionProduct(
            child.appointmentId,
            child.appointmentProposalId,
            child.appointmentProposalOptionId,
            child.appointmentProposalOptionProductId
          )
        )
      );

      await deleteAppointmentProposalOptionProduct(
        product.appointmentId,
        product.appointmentProposalId,
        product.appointmentProposalOptionId,
        product.appointmentProposalOptionProductId
      );
    },
    [deleteAppointmentProposalOptionProduct, getChildren]
  );

  return { addSubItem, deleteProductCascading, getChildren };
};
